<script setup lang="ts">
import {
  computed,
  useRuntimeConfig,
  useRouter,
  useMaps,
  useSpots,
  useCreators,
  callOnce,
  watch,
  clippedText,
  usePlaces,
} from "#imports";
import MPSocialShare from "./MPShare/MPSocialShare.vue";
import MPButton from "~/components/forms/MPButton.vue";

const props = defineProps<{
  version: "map" | "mapready" | "spot" | "creator";
  id: string;
}>();

const config = useRuntimeConfig();
const router = useRouter();
const mapStore = useMaps();
const spotStore = useSpots();
const creatorStore = useCreators();
const placeStore = usePlaces();

const spot = computed(() => {
  if (props.version === "spot") {
    const s = spotStore.getById(props.id).value!;
    const place = placeStore.getById(s.place_id).value;
    return {
      ...s,
      place,
    };
  }
  return null;
});

const map = computed(() => {
  if (props.version === "map" || props.version === "mapready") {
    return mapStore.getById(props.id).value;
  } else if (props.version === "spot") {
    return mapStore.getById(spot.value?.map_id).value;
  }
  return null;
});

const creator = computed(() => {
  if (props.version === "creator") {
    return creatorStore.getById(props.id).value;
  } else {
    return creatorStore.getById(map.value?.user_id).value;
  }
});

const loadData = async () => {
  if (props.version === "creator") {
    await creatorStore.fetchById(props.id);
  } else if (props.version === "map" || props.version === "mapready") {
    const map = await mapStore.fetchById(props.id);
    await creatorStore.fetchById(map.value!.user_id);
  } else if (props.version === "spot") {
    const spot = await spotStore.fetchById(props.id);
    const map = await mapStore.fetchById(spot.value!.map_id);
    await creatorStore.fetchById(map.value!.user_id);
  }
};

await callOnce(`MPShare-${props.version}-${props.id}`, loadData, {
  mode: "navigation",
});
watch(() => props, loadData);

const shareUrl = computed(() => {
  if (props.version === "spot") {
    return `${config.public.baseURL}/maps/${map.value?.current_slug}/spots/${spot.value?.current_slug}`;
  } else if (props.version === "map" || props.version === "mapready") {
    return `${config.public.baseURL}/maps/${map.value?.current_slug}`;
  } else {
    // if (props.version === "creator") {
    return `${config.public.baseURL}/@${creator.value?.mappr_handle}`;
  }
});

const clippedSummary = computed(() =>
  clippedText(spot.value?.summary ?? map.value?.summary ?? "", 90),
);

const title = computed(() => {
  if (props.version === "creator") {
    return creator.value?.mappr_handle;
  } else if (props.version === "spot") {
    return spot.value?.name;
  } else if (props.version === "map") {
    return map.value?.title;
  } else {
    //if (props.version === "mapready")
    return "Your map is ready";
  }
});

const subtitle = computed(() => {
  if (props.version === "creator") {
    return "Share creator:";
  } else if (props.version === "spot") {
    return "Share spot:";
  } else if (props.version === "map") {
    return "Share map:";
  } else {
    //if (props.version === "mapready")
    return "Creator Onboarding";
  }
});
</script>

<template>
  <article class="share-container">
    <header>
      <div v-if="props.version === 'mapready'" class="step-indicators">
        <div class="step-dot completed" />
        <div class="step-dot completed" />
        <div class="step-dot current" />
        <div class="step-dot" />
      </div>
      <h5
        class="sub-header"
        :class="{ mapready: props.version === 'mapready' }"
      >
        {{ subtitle }}
      </h5>
      <h3 class="main-header">
        {{ title }}
      </h3>
      <p v-if="props.version === 'mapready'">
        Take a moment to review your Map. You can update it any time from your
        profile
      </p>
    </header>
    <main
      v-if="props.version == 'map'"
      class="mapcard-container"
      :style="{ 'background-image': 'url(' + map?.cover_image_url + ')' }"
    >
      <div class="spot-summary">
        <h4>{{ clippedSummary }}</h4>
        <p class="map-by">By: {{ creator?.mappr_handle }}</p>
        <span class="map-spots-count-pill"
          >{{ map?.spotIds.length }} Spots</span
        >
      </div>
    </main>
    <main
      v-if="props.version == 'spot'"
      class="mapcard-container"
      :style="{
        'background-image': 'url(' + spot?.cover_image_url + ')',
      }"
    >
      <div class="spot-summary">
        <p v-if="spot?.place">{{ spot.place.fullName }}</p>
        <h4>{{ clippedSummary }}</h4>
        <p class="map-by">By: {{ creator?.mappr_handle }}</p>
      </div>
    </main>
    <main
      v-if="props.version == 'creator'"
      :style="{
        'background-image': 'url(' + creator?.profile_image_url + ')',
      }"
      class="mapcard-container"
    >
      <span class="spot-summary">{{ creator?.mapIds.length }} Maps</span>
    </main>

    <footer v-if="props.version !== 'mapready'" class="share-buttons">
      <MPSocialShare network="facebook" :url="shareUrl" :title="title!" />
      <MPSocialShare network="instagram" :url="shareUrl" :title="title!" />
      <MPSocialShare network="bluesky" :url="shareUrl" :title="title!" />
      <MPSocialShare network="x" :url="shareUrl" :title="title!" />
      <MPSocialShare network="threads" :url="shareUrl" :title="title!" />
      <MPSocialShare network="whatsapp" :url="shareUrl" :title="title!" />
      <MPSocialShare network="email" :url="shareUrl" :title="title!" />
    </footer>
    <footer v-else>
      <div class="share-button-container">
        <MPButton
          label="Add Payout Details"
          version="primary"
          class="payout-button"
          size="large"
          backgroundColor="var(--color-indigo-400)"
          @click="router.push('/studio/payouts')"
        />
      </div>
    </footer>
  </article>
</template>

<style scoped lang="postcss">
.share-button-container {
  .payout-button {
    border-radius: 2rem;
  }
}

.share-container {
  max-width: 24rem;
  min-width: 22rem;
  padding: 2rem;
  padding-top: 1.25rem;
  header {
    margin-bottom: 1rem;
    text-align: center;

    .step-indicators {
      display: flex;
      justify-content: center;
      gap: 0.75rem;
      margin-bottom: 1rem;
    }

    .step-dot {
      width: 1rem;
      height: 1rem;
      border-radius: 50%;
      border: 2px solid var(--color-slate-300);

      &.completed {
        background-color: var(--color-green-400);
        border-color: var(--color-green-400);
      }

      &.current {
        background-color: var(--color-slate-200);
        border-color: var(--color-slate-300);
      }
    }

    .sub-header {
      color: var(--color-rose-500);
      margin: 0;
      padding: 0;
      margin-bottom: 0.75rem;

      &.mapready {
        color: var(--color-indigo-300);
        margin-bottom: 1rem;
      }
    }
    .main-header {
      color: var(--color-slate-600);
      margin: 0;
      padding: 0;
    }
  }
  main {
    margin-block: 2rem;
    margin: 0 auto;
  }

  footer {
    text-align: center;
    margin-top: 1rem;
  }
}

.map-card-cover {
  border-radius: 1rem;
}

.mapcard-container {
  position: relative;
  display: flex;
  min-height: 10rem;
  aspect-ratio: 3/4;
  background-size: cover;
  border-radius: 1rem;
}

.mapcard-container::before {
  position: absolute;
  content: "";
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 10%,
    rgba(0, 0, 0, 0.8) 100%
  );
  border-radius: 1rem;
}

.map-spots-count-pill {
  border-radius: 2rem;
  padding: 0.25rem 1rem;
  color: white;
  z-index: 2;
  border: 1px solid var(--color-slate-200);
}

.map-by {
  color: var(--color-rose-400);
  z-index: 2;
}

.map-summary {
  color: white;
}

.spot-summary {
  place-self: end;
  color: white;
  z-index: 2;
  padding: 2rem;

  h4 {
    margin: 0;
    margin-bottom: 0.5rem;
  }

  p {
    margin: 0;
    margin-bottom: 1rem;
  }
}

.share-spot-card {
  margin-block: 1rem;
}

.creator-container {
  padding-bottom: 2rem;
}

.share-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;

  .social-share-button {
    color: white;
  }

  .social-share-button:hover {
    opacity: 0.9;
  }
}
</style>
